import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@repo/ui";
import { ROUTESWEB } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { createSyncAdapter } from "../adapters/syncAdapter";
import { useModuleData } from "../context/ModuleDataContext";
import { TESTS_ROUTES } from "../routes";
import type { ExamRouteState } from "../types";
import { useSubmitFeedback, type ExperienceRating } from "./useSubmitFeedback";

export const usePdfViewerSubmission = (
  examId: string,
  examState: ExamRouteState | null,
) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation("tests");
  const { profilesData } = useModuleData();
  const syncAdapter = useMemo(() => createSyncAdapter(), []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRateExperienceOpen, setIsRateExperienceOpen] = useState(false);

  const studentId =
    profilesData?.unified_account_details?.profiles?.find(
      (profile) => profile.is_default_profile,
    )?.uam_id != null
      ? String(
          profilesData.unified_account_details.profiles.find(
            (profile) => profile.is_default_profile,
          )?.uam_id,
        )
      : profilesData?.unified_account_details?.profiles?.[0]?.uam_id != null
        ? String(profilesData.unified_account_details.profiles[0].uam_id)
        : "";

  const { submitFeedback, isPending: isFeedbackSubmitting } = useSubmitFeedback(
    {
      userId: studentId,
      examId,
    },
  );

  const goBackToSource = useCallback(() => {
    if (examState?.pathToNav) {
      navigate(examState.pathToNav, { replace: true });
      return;
    }

    navigate(`/${ROUTESWEB.APP.TESTS}`, { replace: true });
  }, [examState?.pathToNav, navigate]);

  const goToReport = useCallback(() => {
    navigate(
      `/${ROUTESWEB.APP.TESTS}/${TESTS_ROUTES.REPORT}${window.location.search}`,
      {
        replace: true,
        state: { examId, examState },
      },
    );
  }, [examId, examState, navigate]);

  const handleConfirmSubmit = useCallback(async () => {
    if (!examId || !studentId) {
      toast({
        title: t("exam_submit_failed"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    setIsSubmitting(true);

    try {
      await new Promise((r) => setTimeout(r, Math.random() * 5000 + 25000));
      await syncAdapter.submitExam(examId, studentId, "manual_submit_trusted");
      setIsRateExperienceOpen(true);
      return true;
    } catch (error) {
      toast({
        title: t("exam_submit_failed"),
        description:
          error instanceof Error ? error.message : t("exam_submit_failed"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      goBackToSource();
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [examId, studentId, syncAdapter, t, toast, goBackToSource]);

  const handleFeedbackSubmit = useCallback(
    (experience: ExperienceRating, comments: string[], feedback?: string) => {
      submitFeedback(experience, comments, feedback)
        .then(() => {
          setIsRateExperienceOpen(false);
          goToReport();
        })
        .catch(() => {
          setIsRateExperienceOpen(false);
          goToReport();
        });
    },
    [goToReport, submitFeedback],
  );

  const handleRateExperienceClose = useCallback(() => {
    setIsRateExperienceOpen(false);
    goToReport();
  }, [goToReport]);

  const handleResumeExam = useCallback(() => {
    if (!examState) {
      navigate(`/${ROUTESWEB.APP.TESTS}`, { replace: true });
      return;
    }

    navigate(`/${ROUTESWEB.APP.TESTS}/${TESTS_ROUTES.EXAM}`, {
      state: {
        ...examState,
        fromExam: true,
      },
      replace: true,
    });
  }, [examState, navigate]);

  return {
    isSubmitting,
    handleConfirmSubmit,
    handleResumeExam,
    isRateExperienceOpen,
    handleFeedbackSubmit,
    handleRateExperienceClose,
    isFeedbackSubmitting,
  };
};
